import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import { suggestLabelValueMapping } from "./PaymentPlatformLabelDialog";

const refKey = (reference) => reference
  ? `${reference.fileName}\u0000${reference.sheetName}\u0000${reference.column}`
  : "";
const ML_KEYS = new Set(["fico", "businessRisk", "businessMonths", "averageMonthlyVolume", "disputes", "transactions", "logins", "productViews"]);
const IDENTITY_KEYS = ["externalId", "companyName", "applicationId", "registrationNumber", "website", "email"];
const OUTPUT_TEMPLATES = [
  { id: "repayment_outcome", name: "Positive / negative outcome", type: "boolean" },
  { id: "recommended_advance", name: "Recommended advance amount", type: "number" },
  { id: "pd_score", name: "PD score", type: "ordinal", min: 1, max: 5 },
];

function FieldSelect({ field, value, options, onChange }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "250px 1fr" }, gap: 1.5, alignItems: "center", py: 0.75 }}>
      <Box>
        <Typography sx={{ fontWeight: 800, color: "#17365f", fontSize: 13.5 }}>{field.label}</Typography>
        <Chip
          size="small"
          label={field.importance}
          color={field.importance === "required" ? "primary" : "default"}
          sx={{ height: 20, fontSize: 10.5, textTransform: "capitalize" }}
        />
      </Box>
      <FormControl fullWidth size="small">
        <InputLabel>{field.label} column</InputLabel>
        <Select
          label={`${field.label} column`}
          value={refKey(value)}
          onChange={(event) => onChange(options.find((option) => refKey(option) === event.target.value) || null)}
        >
          <MenuItem value=""><em>Not mapped</em></MenuItem>
          {options.map((option) => (
            <MenuItem key={refKey(option)} value={refKey(option)}>
              {option.fileName} · {option.sheetName} · {option.column}
              <span style={{ marginLeft: "auto", color: "#64748b", paddingLeft: 12 }}>{option.coverage}%</span>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

export default function PaymentPlatformColumnMappingDialog({
  open,
  preview,
  currentMapping,
  busy = false,
  onClose,
  onApply,
  onSaveDraft,
}) {
  const profiles = useMemo(() => preview?.profiles || [], [preview]);
  const mappingFields = useMemo(() => preview?.mappingFields || [], [preview]);
  const options = useMemo(
    () => profiles.flatMap((profile) => (profile.columns || []).map((column) => ({
      fileName: profile.fileName,
      sheetName: profile.sheetName,
      column: column.name,
      coverage: column.coverage,
      distinctCount: column.distinctCount,
      values: column.values || [],
      valuesTruncated: column.valuesTruncated,
    }))),
    [profiles],
  );
  const [fields, setFields] = useState({});
  const [outputs, setOutputs] = useState(OUTPUT_TEMPLATES.map((output) => ({ ...output, enabled: false })));
  const [runAll, setRunAll] = useState(true);

  const initialize = (mapping) => {
    const nextFields = mapping?.fields || preview?.suggestedMapping?.fields || {};
    setFields(nextFields);
    const storedOutputs = mapping?.outputs?.length
      ? mapping.outputs
      : (mapping?.label ? [{ ...OUTPUT_TEMPLATES[0], ...mapping.label }] : []);
    setOutputs(OUTPUT_TEMPLATES.map((template) => {
      const stored = storedOutputs.find((output) => output.id === template.id || output.type === template.type);
      if (!stored) return { ...template, enabled: false };
      const referenceOption = options.find((option) => refKey(option) === refKey(stored));
      const displayValue = (storedValue) =>
        referenceOption?.values?.find(
          (item) => String(item.value).trim().toLowerCase() === String(storedValue).trim().toLowerCase(),
        )?.value || storedValue;
      return {
        ...template,
        ...stored,
        enabled: true,
        positiveValues: (stored.positiveValues || []).map(displayValue),
        negativeValues: (stored.negativeValues || []).map(displayValue),
      };
    }));
    setRunAll(mapping?.runAll !== false);
  };

  useEffect(() => {
    if (open && preview) initialize(currentMapping);
  }, [open, preview, currentMapping]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateOutput = (id, changes) => {
    setOutputs((current) => current.map((output) => output.id === id ? { ...output, ...changes } : output));
  };
  const chooseOutputColumn = (output, reference) => {
    const suggestion = output.type === "boolean" ? suggestLabelValueMapping(reference?.values || []) : {};
    updateOutput(output.id, {
      ...(reference || { fileName: "", sheetName: "", column: "" }),
      ...(output.type === "boolean" ? suggestion : {}),
    });
  };

  const identityReady = IDENTITY_KEYS.some((key) => fields[key]);
  const advanceReady = Boolean(fields.requestedAmount || fields.approvedAmount);
  const featureCount = [...ML_KEYS].filter((key) => fields[key]).length;
  const enabledOutputs = outputs.filter((output) => output.enabled);
  const outputReady = (output) => Boolean(
    output.column
    && (output.type !== "boolean" || (output.positiveValues?.length && output.negativeValues?.length)),
  );
  const readyOutputs = enabledOutputs.filter(outputReady);
  const ready = identityReady && advanceReady && featureCount >= 2 && readyOutputs.length > 0;
  const mappedCount = Object.keys(fields).filter((key) => fields[key]).length;

  const mappingPayload = () => ({
    fields,
    outputs: enabledOutputs.filter((output) => output.column).map(({ enabled, ...output }) => output),
    runAll,
  });
  const apply = () => onApply(mappingPayload());

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 900, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
        MCA data column mapping
        <Button
          startIcon={<AutoFixHighOutlinedIcon />}
          onClick={() => initialize({ fields: preview?.suggestedMapping?.fields || {}, outputs: [] })}
          disabled={busy}
        >
          Auto-map columns
        </Button>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#f7f9fc" }}>
        <Alert severity={ready ? "success" : "warning"} sx={{ mb: 2 }}>
          {ready
            ? `Ready to rebuild: ${mappedCount} input columns and ${readyOutputs.length} output ${readyOutputs.length === 1 ? "model" : "models"} are configured.`
            : "Complete identity, advance size, at least two ML features, and at least one output target before rebuilding."}
        </Alert>

        {["Identity", "Advance & pricing", "ML features"].map((group) => (
          <Paper key={group} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 900, color: "#0b2a4a", mb: 0.5 }}>{group}</Typography>
            <Typography sx={{ color: "#64748b", fontSize: 12.5, mb: 1 }}>
              {group === "Identity" && "Columns used to resolve the same merchant across every uploaded file."}
              {group === "Advance & pricing" && "Requested and funded advance size, payment volume, term and pricing."}
              {group === "ML features" && "Underwriting and platform signals available at decision time."}
            </Typography>
            {mappingFields.filter((field) => field.group === group).map((field) => (
              <FieldSelect
                key={field.key}
                field={field}
                value={fields[field.key]}
                options={options}
                onChange={(reference) => setFields((current) => ({ ...current, [field.key]: reference }))}
              />
            ))}
          </Paper>
        ))}

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", mb: 1.5 }}>
            <Box>
          <Typography sx={{ fontWeight: 900, color: "#0b2a4a" }}>Model output labels</Typography>
          <Typography sx={{ color: "#64748b", fontSize: 12.5, mb: 1.5 }}>
            Enable one or more predictions. Every enabled output is trained and evaluated separately using the same merchant features.
          </Typography>
            </Box>
            <FormControlLabel control={<Switch checked={runAll} onChange={(event) => setRunAll(event.target.checked)} />} label="Run all configured outputs" />
          </Box>
          <Stack spacing={1.5}>
          {outputs.map((output) => {
            const selectedOption = options.find((option) => refKey(option) === refKey(output)) || null;
            return (
            <Paper key={output.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: output.enabled ? "#fff" : "#f8fafc" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center", mb: output.enabled ? 1.5 : 0 }}>
                <Box>
                  <Typography sx={{ fontWeight: 850, color: "#17365f" }}>{output.name}</Typography>
                  <Typography sx={{ color: "#64748b", fontSize: 11.5 }}>
                    {output.type === "boolean" ? "Boolean classification" : output.type === "ordinal" ? "Native ordinal score · 1–5" : "Numeric regression · currency amount"}
                  </Typography>
                </Box>
                <FormControlLabel control={<Switch checked={output.enabled} onChange={(event) => updateOutput(output.id, { enabled: event.target.checked })} />} label={output.enabled ? "Included" : "Not included"} />
              </Box>
              {output.enabled && <>
                <FormControl fullWidth size="small" sx={{ mb: output.type === "boolean" ? 1.5 : 0 }}>
                  <InputLabel>Output column</InputLabel>
                  <Select label="Output column" value={refKey(output)} onChange={(event) => chooseOutputColumn(output, options.find((option) => refKey(option) === event.target.value) || null)}>
                    <MenuItem value=""><em>Not mapped</em></MenuItem>
                    {options.map((option) => <MenuItem key={refKey(option)} value={refKey(option)}>{option.fileName} · {option.sheetName} · {option.column}<span style={{ marginLeft: "auto", color: "#64748b", paddingLeft: 12 }}>{option.distinctCount} values</span></MenuItem>)}
                  </Select>
                </FormControl>
                {output.type === "boolean" && <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
            <FormControl fullWidth size="small" disabled={!selectedOption}>
              <InputLabel>Positive / performing values</InputLabel>
              <Select
                multiple
                label="Positive / performing values"
                value={output.positiveValues || []}
                onChange={(event) => {
                  const next = event.target.value;
                  updateOutput(output.id, { positiveValues: next, negativeValues: (output.negativeValues || []).filter((value) => !next.includes(value)) });
                }}
                renderValue={(selected) => selected.join(", ")}
              >
                {(selectedOption?.values || []).map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    <Checkbox checked={(output.positiveValues || []).includes(item.value)} />
                    <ListItemText primary={item.value} secondary={`${item.count.toLocaleString()} rows`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" disabled={!selectedOption}>
              <InputLabel>Negative / non-performing values</InputLabel>
              <Select
                multiple
                label="Negative / non-performing values"
                value={output.negativeValues || []}
                onChange={(event) => {
                  const next = event.target.value;
                  updateOutput(output.id, { negativeValues: next, positiveValues: (output.positiveValues || []).filter((value) => !next.includes(value)) });
                }}
                renderValue={(selected) => selected.join(", ")}
              >
                {(selectedOption?.values || []).map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    <Checkbox checked={(output.negativeValues || []).includes(item.value)} />
                    <ListItemText primary={item.value} secondary={`${item.count.toLocaleString()} rows`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>}
          {output.type === "ordinal" && <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mt: 1.5 }}><TextField size="small" type="number" label="Minimum score" value={output.min} onChange={(event) => updateOutput(output.id, { min: Number(event.target.value) })} /><TextField size="small" type="number" label="Maximum score" value={output.max} onChange={(event) => updateOutput(output.id, { max: Number(event.target.value) })} /></Box>}
          </>}
            </Paper>
          );})}
          </Stack>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        {onSaveDraft && (
          <Button
            variant="outlined"
            onClick={() => onSaveDraft(mappingPayload())}
            disabled={busy || mappedCount === 0}
          >
            {busy ? "Saving…" : "Save mapping"}
          </Button>
        )}
        <Button variant="contained" onClick={apply} disabled={busy || !ready}>
          {busy ? "Queuing rebuild…" : "Save & rebuild assessments"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
